import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core'
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms'
import { Uuid } from '@ftr/contracts/type/shared'
import { CheckboxItem, CheckboxTreeComponent, CheckboxTreeItem, SearchInputClear } from '@ftr/forms'
import { ApiResult, ArrayUtils, DestroySubscribers, tapData } from '@ftr/foundation'
import { BehaviorSubject, Observable, combineLatest, debounceTime, switchMap, takeUntil, tap } from 'rxjs'
import { CheckboxTreeData, CheckboxTreeFilter } from '../CheckboxTreeFilter'
import {
  ComplexFilterData,
  FormValueType,
  NestingDisplayMode,
  SEARCH_DEBOUNCE,
  mapFormValueToFilter,
} from '../ComplexFilter'
import { ComplexFilterService, checkMaxSelectedOptions } from '../complex-filter.service'

@Component({
  selector: 'ftr-complex-filter-checkbox-tree',
  templateUrl: './complex-filter-checkbox-tree.component.html',
  styleUrls: ['./complex-filter-checkbox-tree.component.css'],
})
export class ComplexFilterCheckboxTreeComponent extends DestroySubscribers implements OnInit {
  /**
   * Filter configuration, must be specific to the checkbox-tree filter type
   */
  @Input() filter: CheckboxTreeFilter
  @Input() toggled: Observable<boolean>
  @Input() visibilityToggled: Observable<boolean>
  @Input() applyImmediately: boolean
  @Input() nestingDisplayMode: NestingDisplayMode
  @Output() filterChanged = new EventEmitter<ComplexFilterData>()
  @ViewChild(CheckboxTreeComponent) checkboxTreeComponent: CheckboxTreeComponent

  internalFormGroup: UntypedFormGroup = new UntypedFormGroup({})
  searchControl: UntypedFormControl = new UntypedFormControl('')
  viewModel: ApiResult<CheckboxTreeData[]>
  readonly searchInputClear = SearchInputClear
  /**
   * This value changes with the search term after debounceTime has passed.
   * It allows us to tell the difference between an empty initial state and an empty search result.
   */
  searchTerm: string | undefined
  // The number of visible filter options
  totalFilterCount: number
  // The options used to create checkboxes
  options: CheckboxTreeData[] = []
  // To refresh the filter value when options get an update
  refreshFilter: BehaviorSubject<void> = new BehaviorSubject<void>(undefined)
  // To refresh filter options following a search or an API error
  refreshOptions: BehaviorSubject<void> = new BehaviorSubject<void>(undefined)
  // The data to pass to the checkbox tree component
  checkboxes = new BehaviorSubject<CheckboxTreeItem[]>([])

  private lastValue: ComplexFilterData

  constructor(private readonly complexFilterService: ComplexFilterService) {
    super()
  }

  ngOnInit(): void {
    this.setupViewModel()
    this.setupSearchControl()
    this.listenToFilterChange()
    this.listenToVisibilityChange()
  }

  setupViewModel(): void {
    this.viewModel = this.refreshOptions.pipe(
      switchMap(() =>
        this.filter.options().pipe(
          tapData(options => {
            this.buildInternalFormControls(options)
            this.refreshFilter.next(undefined)
          }),
        ),
      ),
    )
  }

  setupSearchControl(): void {
    this.searchControl.valueChanges
      .pipe(takeUntil(this.finalize), debounceTime(SEARCH_DEBOUNCE))
      .subscribe(searchTerm => this.search(searchTerm))

    this.toggled.pipe(takeUntil(this.finalize)).subscribe(toggled => {
      if (!toggled) {
        this.searchControl.reset()
      }
    })
  }

  listenToFilterChange(): void {
    combineLatest([this.complexFilterService.listenToFilterChange([this.filter]), this.refreshFilter])
      .pipe(takeUntil(this.finalize))
      .subscribe(([filters]) => {
        this.internalFormGroup.reset(false, { emitEvent: false })
        const filterValue = filters[this.filter.filterKey] as string[]
        // Patch the form without emitting
        this.internalFormGroup.patchValue(mapFilterToFormValue(filterValue), { emitEvent: false })
        this.lastValue = this.internalFormGroup.value
        this.createCheckboxes(filterValue)
      })
  }

  onValueChange([id, value]: readonly [Uuid, boolean]): void {
    this.internalFormGroup.patchValue({ [id]: value }, { emitEvent: false })
    const filterValue = mapFormValueToFilter(this.internalFormGroup.value)
    this.createCheckboxes(filterValue)
    this.filterChanged.emit({ [this.filter.filterKey]: filterValue })
  }

  private search(searchTerm: string | undefined): void {
    this.searchTerm = searchTerm

    if (this.checkboxTreeComponent?.filter) {
      this.checkboxTreeComponent.filter.next(searchTerm || '')
    }
  }

  private createCheckboxes(filterValue: string[]): void {
    checkMaxSelectedOptions(this.internalFormGroup, filterValue, this.filter.maxSelectedOptions)

    this.checkboxes.next(
      this.options.map(
        option =>
          new CheckboxTreeItem(
            option.name,
            option.id,
            option.children.map(
              child =>
                new CheckboxItem(
                  child.name,
                  child.id,
                  this.internalFormGroup.value[child.id] === true,
                  false,
                  false,
                  this.internalFormGroup.controls[child.id].disabled,
                ),
            ),
            false,
            this.internalFormGroup.controls[option.id].disabled &&
              !option.children.some(c => !!this.internalFormGroup.value[c.id]),
          ),
      ),
    )
  }

  private buildInternalFormControls(options: CheckboxTreeData[]): void {
    this.options = [...options]

    options.forEach(option => {
      this.internalFormGroup.addControl(option.id, new UntypedFormControl(), { emitEvent: false })
      option.children.forEach(child => {
        this.internalFormGroup.addControl(child.id, new UntypedFormControl(), { emitEvent: false })
      })
    })
  }

  /**
   * This is to ensure the value of the filter resets to the previous one
   * when we change it manually and close the modal without applying.
   */
  private listenToVisibilityChange(): void {
    this.visibilityToggled
      .pipe(
        takeUntil(this.finalize),
        tap(visible => {
          const filterValue = mapFormValueToFilter(this.lastValue)
          const isSameValue = ArrayUtils.shallowEquals(mapFormValueToFilter(this.internalFormGroup.value), filterValue)

          if (!visible && !this.applyImmediately && !isSameValue) {
            this.internalFormGroup.reset(this.lastValue, { emitEvent: false })
            this.createCheckboxes(filterValue)

            // Reset checkbox tree selection to avoid unsaved selected items getting stuck
            if (this.checkboxTreeComponent) {
              this.checkboxTreeComponent.ngOnInit()
            }
            this.filterChanged.emit({ [this.filter.filterKey]: filterValue })
          }
        }),
      )
      .subscribe()
  }
}

function mapFilterToFormValue(filters: string[]): FormValueType {
  return filters.reduce((acc, val) => {
    acc[val] = true
    return acc
  }, {} as FormValueType)
}
